// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import deletePaymentProfile from "@insite/client-framework/Store/Data/PaymentProfiles/Handlers/DeletePaymentProfile";
import updatePaymentProfile from "@insite/client-framework/Store/Data/PaymentProfiles/Handlers/UpdatePaymentProfile";
import updateEditModal from "@insite/client-framework/Store/Pages/SavedPayments/Handlers/UpdateEditModal";
import translate from "@insite/client-framework/Translate";
import { AccountPaymentProfileModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { PaymentProfilesContext } from "@insite/content-library/Pages/SavedPaymentsPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem, LoadingSpinner } from "@insite/mobius/index";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { pluralize } from "@rpsc/XDL/Utilities";
import sortBy from "lodash/sortBy";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updateEditModal,
    updatePaymentProfile,
    deletePaymentProfile,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps>;

export interface SavedPaymentsCardListStyles {
    headerContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    sortByGridItem?: GridItemProps;
    sortBySelect?: SelectProps;
    cardHeaderWrapper?: InjectableCss;
    cardTypeImage?: LazyImageProps;
    descriptionText?: TypographyPresentationProps;
    cardInfoContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    nameOnCardLabelText?: TypographyPresentationProps;
    nameOnCardText?: TypographyPresentationProps;
    expirationLabelText?: TypographyPresentationProps;
    expirationText?: TypographyPresentationProps;
    centerColumnGridItem?: GridItemProps;
    billingAddressLabelText?: TypographyPresentationProps;
    billingAddressText?: TypographyPresentationProps;
    rightColumnGridItem?: GridItemProps;
    linksContainer?: GridContainerProps;
    useAsDefaultGridItem?: GridItemProps;
    useAsDefaultButton?: ButtonPresentationProps;
    editLinkGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    deleteLinkGridItem?: GridItemProps;
    deleteLink?: LinkPresentationProps;
    deleteCardModal?: TwoButtonModalStyles;
    // Xng-Changes:
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    loadingSpinner?: LoadingSpinnerProps;
    sortBySelectWrapper?: InjectableCss;
    cardsCount?: TypographyPresentationProps;
    cardsContainer?: GridContainerProps;
    cardGridItem?: GridItemProps;
    cardWrapper?: InjectableCss;
    cardDescriptionWrapper?: InjectableCss;
    maskedCardNumberText?: TypographyPresentationProps;
    cardBottomWrapper?: InjectableCss;
    defaultCardMarker?: InjectableCss;
    defaultCardMarkerText?: TypographyPresentationProps;
    nameOnCardAndExpDateWrapper?: InjectableCss;
    expDateText?: TypographyPresentationProps;
    actionsWrapper?: InjectableCss;
    setDefaultCheckbox?: CheckboxPresentationProps;
    actionButtonsWrapper?: InjectableCss;
    editButton?: ButtonPresentationProps;
    deleteButton?: ButtonPresentationProps;
}

// Xng-Changes: please check SavedPaymentsCardListExtensions.ts for styles
export const cardListStyles: SavedPaymentsCardListStyles = {
    headerContainer: { gap: 20 },
    titleGridItem: { width: [12, 12, 5, 5, 5] },
    titleText: {
        variant: "h3",
        as: "h2",
        css: css`
            margin-bottom: 0;
        `,
    },
    sortByGridItem: { width: [12, 12, 7, 7, 7] },
    sortBySelect: {
        labelPosition: "left",
        labelProps: {
            css: css`
                width: auto;
            `,
        },
        cssOverrides: {
            formField: css`
                margin-bottom: 1rem;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            justify-content: flex-start;
                        `,
                        css`
                            justify-content: flex-start;
                        `,
                        css`
                            justify-content: flex-end;
                        `,
                        css`
                            justify-content: flex-end;
                        `,
                        css`
                            justify-content: flex-end;
                        `,
                    ])}
            `,
        },
    },
    cardHeaderWrapper: {
        css: css`
            display: flex;
        `,
    },
    cardTypeImage: {
        imgProps: {
            style: {
                width: "auto",
            },
        },
    },
    descriptionText: {
        css: css`
            margin: 5px 10px;
            font-size: 15px;
        `,
    },
    leftColumnGridItem: {
        width: [6, 6, 4, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    nameOnCardLabelText: {
        weight: "bold",
        css: css`
            margin-bottom: 4px;
        `,
    },
    nameOnCardText: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    expirationLabelText: {
        weight: "bold",
        css: css`
            margin-bottom: 4px;
        `,
    },
    centerColumnGridItem: {
        width: [6, 6, 4, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    billingAddressLabelText: {
        weight: "bold",
        css: css`
            margin-bottom: 4px;
        `,
    },
    billingAddressText: {
        css: css`
            width: 100%;
        `,
    },
    rightColumnGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
            align-items: center;
        `,
    },
    linksContainer: {
        gap: 20,
        css: css`
            width: 100%;
        `,
    },
    useAsDefaultGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            margin-bottom: 20px;
        `,
    },
    useAsDefaultButton: {
        variant: "secondary",
        css: css`
            width: 100%;
            padding: 0 15px;
        `,
    },
    editLinkGridItem: {
        width: [6, 6, 12, 12, 12],
        css: css`
            justify-content: center;
            margin-bottom: 20px;
        `,
    },
    deleteLinkGridItem: {
        width: [6, 6, 12, 12, 12],
        css: css`
            justify-content: center;
            margin-bottom: 20px;
        `,
    },
};

const styles = cardListStyles;

const getImageName = (cardType: string) => {
    if (cardType.toLowerCase() === "american express") {
        return "amex";
    }
    if (cardType.toLowerCase() === "discover") {
        return "discover";
    }
    if (cardType.toLowerCase() === "mastercard") {
        return "mc";
    }
    if (cardType.toLowerCase() === "visa") {
        return "visa";
    }
    return "";
};

const SavedPaymentsCardList: React.FC<Props> = ({ updateEditModal, updatePaymentProfile, deletePaymentProfile }) => {
    const [sortByProperty, setSortByProperty] = React.useState("description");
    const sortByChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSortByProperty(event.target.value);
    };

    // Xng-Change: add spinner state
    const [isLoading, setIsLoading] = React.useState(false);
    const toasterContext = React.useContext(ToasterContext);
    const useAsDefaultClickHandler = (paymentProfile: AccountPaymentProfileModel, isDefault: boolean) => {
        setIsLoading(true);
        const paymentProfileToUpdate = { ...paymentProfile, isDefault };
        updatePaymentProfile({
            paymentProfile: paymentProfileToUpdate,
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Default Card Updated"), messageType: "success" });
            },
            onComplete(resultProps) {
                setIsLoading(false);
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const editClickHandler = (paymentProfile: AccountPaymentProfileModel) => {
        updateEditModal({ paymentProfile, modalIsOpen: true });
    };

    const [deleteCardModalIsOpen, setDeleteCardModalIsOpen] = React.useState(false);
    const [paymentProfileToDelete, setPaymentProfileToDelete] = React.useState<AccountPaymentProfileModel | null>(null);
    const deleteCancelHandler = () => {
        setDeleteCardModalIsOpen(false);
    };

    const deleteSubmitHandler = () => {
        if (!paymentProfileToDelete) {
            return;
        }

        deletePaymentProfile({
            paymentProfileId: paymentProfileToDelete.id,
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Card Deleted"), messageType: "success" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const deleteClickHandler = (paymentProfile: AccountPaymentProfileModel) => {
        setPaymentProfileToDelete(paymentProfile);
        setDeleteCardModalIsOpen(true);
    };

    const paymentProfilesDataView = useContext(PaymentProfilesContext);

    if (!paymentProfilesDataView.value || paymentProfilesDataView.value?.length === 0) {
        return null;
    }

    const sortedList = sortBy(paymentProfilesDataView.value, sortByProperty);

    // Xng-Change: complete style and structure change, add spinner
    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.loadingSpinner} />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.sortBySelectWrapper}>
                <Select
                    {...styles.sortBySelect}
                    label={translate("Sort by")}
                    value={sortByProperty}
                    onChange={sortByChangeHandler}
                    data-test-selector="sortBy"
                >
                    <option value="description">{translate("Card Nickname")}</option>
                    <option value="maskedCardNumber">{translate("Card Number")}</option>
                    <option value="cardType">{translate("Card Type")}</option>
                </Select>
            </StyledWrapper>
            <GridContainer {...styles.cardsContainer}>
                {sortedList.map((savedPayment, index) => (
                    <GridItem {...styles.cardGridItem} key={index}>
                        <StyledWrapper {...styles.cardWrapper}>
                            <StyledWrapper
                                {...styles.cardHeaderWrapper}
                                data-test-selector={`cardHeader-${savedPayment.id}`}
                            >
                                <StyledWrapper {...styles.cardDescriptionWrapper}>
                                    <Typography {...styles.descriptionText} as="h4">
                                        {savedPayment.description ||
                                            `${savedPayment.cardType} ${translate(
                                                "ending in",
                                            )} ${savedPayment.maskedCardNumber.substring(
                                                savedPayment.maskedCardNumber.length - 4,
                                            )}`}
                                    </Typography>
                                    <Typography {...styles.maskedCardNumberText} as="p">
                                        {savedPayment.maskedCardNumber.substring(
                                            savedPayment.maskedCardNumber.length - 4,
                                        )}
                                    </Typography>
                                </StyledWrapper>
                                <LazyImage
                                    {...styles.cardTypeImage}
                                    src={`/images/card-types/${getImageName(savedPayment.cardType)}.webp`}
                                    altText={`Card type ${savedPayment.cardType}`}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...styles.cardBottomWrapper}>
                                {savedPayment.isDefault && (
                                    <StyledWrapper {...styles.defaultCardMarker}>
                                        <Typography {...styles.defaultCardMarkerText}>
                                            {translate("Default")}
                                        </Typography>
                                    </StyledWrapper>
                                )}
                                <StyledWrapper {...styles.nameOnCardAndExpDateWrapper}>
                                    <Typography {...styles.nameOnCardText}>{savedPayment.cardHolderName}</Typography>
                                    <Typography {...styles.expDateText}>{savedPayment.expirationDate}</Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.actionsWrapper} className="actionsWrapper">
                                <Checkbox
                                    {...styles.setDefaultCheckbox}
                                    checked={savedPayment.isDefault}
                                    onChange={(_, value) => useAsDefaultClickHandler(savedPayment, value)}
                                    data-test-selector="useAsDefaultButton"
                                >
                                    {translate("Make Default")}
                                </Checkbox>
                                <StyledWrapper {...styles.actionButtonsWrapper}>
                                    <Button
                                        {...styles.editButton}
                                        onClick={() => editClickHandler(savedPayment)}
                                        data-test-selector="editButton"
                                        aria-label={translate("Edit")}
                                    >
                                        {translate("Edit")}
                                    </Button>
                                    <Button
                                        {...styles.deleteButton}
                                        onClick={() => deleteClickHandler(savedPayment)}
                                        data-test-selector="deleteButton"
                                        aria-label={translate("Delete")}
                                    >
                                        {translate("Delete")}
                                    </Button>
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                ))}
            </GridContainer>
            <TwoButtonModal
                {...styles.deleteCardModal}
                modalIsOpen={deleteCardModalIsOpen}
                headlineText={translate("Delete Card")}
                messageText={translate("Are you sure you want to delete this card?")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={deleteCancelHandler}
                onSubmit={deleteSubmitHandler}
                submitTestSelector="submitDeleteCardButton"
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(SavedPaymentsCardList),
    definition: {
        displayName: "Card List",
        group: "Saved Payments",
        allowedContexts: ["SavedPaymentsPage"],
    },
};

export default widgetModule;
